/**
 * Task building blocks shared by Home, the project page, the episode drill-down and Approvals (plan §19.4):
 * "Create task…", the "My tasks" card, the activity feed, presence avatars and the project's task defaults.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { ListTodo, TriangleAlert, Users } from 'lucide-react';
import { TASK_PRIORITIES, type TaskPriority } from '@castfeeds/domain';
import { useT } from '@/i18n';
import { Avatar, Empty, ErrorBox, Field, Modal, QueryView, StatusPill } from '@/components/ui';
import { api } from '@/lib/api/endpoints';
import { useActivity, useMembers, useTaskAssignees, useTasks, useWsMutation } from '@/lib/api/hooks';
import { can } from '@/lib/constants';
import { ago, relativeDay } from '@/lib/format';
import { logInfo } from '@/lib/log';
import { feedText, taskTitle } from '@/lib/tasks';
import { useMembership, useSession } from '@/store/session';
import type { Member, Presence, Task } from '@/lib/api/types';

/** Members who may hold a task (tasks.work), and the current member. */
export function useTaskPeople() {
  const members = useMembers();
  const userId = useSession((s) => s.user?.id);
  const people = (members.data ?? []).filter((m) => can(m.role, 'tasks.work'));
  const me = (members.data ?? []).find((m) => m.userId === userId) ?? null;
  return { people, me, members };
}

export const TASK_STATUS_TONE: Record<string, string> = { todo: 'draft', in_progress: 'processing', needs_you: 'changes', done: 'approved', cancelled: 'rejected', failed: 'failed' };

export function TaskStatusPill({ status }: { status: Task['status'] }) {
  const { td } = useT();
  return <StatusPill status={TASK_STATUS_TONE[status] ?? 'draft'} label={td(`tkStatus_${status}`, status)} />;
}

// ── "Create task…" ───────────────────────────────────────────────

export interface TaskTarget { project_id?: string; episode_id?: string; edit_id?: string; label?: string }

export function CreateTaskDialog({ target, onClose, onCreated }: { target: TaskTarget; onClose: () => void; onCreated?: (t: Task) => void }) {
  const { t, td } = useT();
  const membership = useMembership();
  const { people, me } = useTaskPeople();
  const [title, setTitle] = useState('');
  const [brief, setBrief] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('normal');
  const [assignee, setAssignee] = useState('');
  const [due, setDue] = useState('');
  const assignAnyone = can(membership?.role, 'tasks.assign');
  const choices = assignAnyone ? people : people.filter((p) => p.id === me?.id);
  const create = useWsMutation(
    () =>
      api.tasks.create({
        title: title.trim(), brief: brief.trim(), priority, project_id: target.project_id, episode_id: target.episode_id, edit_id: target.edit_id,
        assignee_member_id: assignee || null, due_at: due ? new Date(`${due}T18:00:00`).toISOString() : null,
      }),
    {
      onSuccess: (task) => {
        logInfo('tasks', `created task=${task.id} target=${JSON.stringify(target)} assignee=${assignee || '-'}`);
        onCreated?.(task);
        onClose();
      },
    },
  );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (title.trim()) create.mutate(undefined);
  };
  return (
    <Modal
      title={t('tkCreateTitle')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>{t('cancel')}</button>
          <button type="submit" form="new-task" className="btn btn-primary" disabled={!title.trim() || create.isPending}>{t('tkCreateBtn')}</button>
        </>
      }
    >
      <form id="new-task" className="stack" style={{ gap: 12 }} onSubmit={submit}>
        {target.label ? <span className="muted small">{t('tkOn')}: {target.label}</span> : null}
        <Field label={t('tkTitle')}>{(p) => <input {...p} className="input" required maxLength={300} value={title} onChange={(e) => setTitle(e.target.value)} />}</Field>
        <Field label={t('tkBrief')}>{(p) => <textarea {...p} className="textarea" rows={3} maxLength={4000} value={brief} onChange={(e) => setBrief(e.target.value)} />}</Field>
        <div className="grid-2" style={{ gap: 12 }}>
          <Field label={t('tkAssignee')}>
            {(p) => (
              <select {...p} className="select" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                <option value="">{t('tkUnassigned')}</option>
                {choices.map((m) => <option key={m.id} value={m.id}>{m.name} · {td(`role_${m.role}`, m.role)}</option>)}
              </select>
            )}
          </Field>
          <Field label={t('tkPriority')}>
            {(p) => (
              <select {...p} className="select" value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
                {TASK_PRIORITIES.map((x) => <option key={x} value={x}>{td(`tkPrio_${x}`, x)}</option>)}
              </select>
            )}
          </Field>
        </div>
        <Field label={t('tkDue')}>{(p) => <input {...p} type="date" className="input" value={due} onChange={(e) => setDue(e.target.value)} />}</Field>
        {create.error ? <ErrorBox error={create.error} /> : null}
      </form>
    </Modal>
  );
}

/** The button, shown only to people who can work on tasks. */
export function CreateTaskButton({ target, className = 'btn' }: { target: TaskTarget; className?: string }) {
  const { t } = useT();
  const membership = useMembership();
  const [open, setOpen] = useState(false);
  if (!can(membership?.role, 'tasks.work')) return null;
  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>
        <ListTodo size={14} aria-hidden />
        {t('tkCreate')}
      </button>
      {open ? <CreateTaskDialog target={target} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

// ── "My tasks" on Home ───────────────────────────────────────────

export function MyTasksCard() {
  const { t, td, locale } = useT();
  const navigate = useNavigate();
  const membership = useMembership();
  const q = useTasks({ assignee: 'me', status: 'open', limit: 6 }, can(membership?.role, 'tasks.view'));
  if (!can(membership?.role, 'tasks.view')) return null;
  return (
    <section className="card" aria-labelledby="mytasks-h">
      <div className="card-head">
        <h2 id="mytasks-h" className="h2">{t('tkMyTasks')}</h2>
        {q.data ? <span className="badge" style={{ background: 'var(--raise)', color: 'var(--text)' }}>{t('tkOpenCount', { n: q.data.counts.todo + q.data.counts.in_progress + q.data.counts.needs_you })}</span> : null}
        <Link to="/tasks?assignee=me" className="push small">{t('tkSeeAll')}</Link>
      </div>
      <QueryView query={q}>
        {(d) =>
          d.items.length === 0 ? (
            <Empty>{t('tkMyTasksEmpty')}</Empty>
          ) : (
            <>
              {d.items.map((task) => (
                <button key={task.id} type="button" className="clickable card-row" onClick={() => navigate(`/tasks/${task.id}`)} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 12, alignItems: 'center' }}>
                  <span className="stack" style={{ gap: 2, minWidth: 0 }}>
                    <span className="ellipsis" style={{ fontSize: 14, fontWeight: 500 }}>{taskTitle({ td }, task)}</span>
                    <span className="muted small ellipsis">
                      {td(`tkType_${task.type.replace('.', '_')}`, task.type)}
                      {task.project ? ` · ${task.project.title}` : ''}
                      {task.due_at ? ` · ${t('tkDueOn', { when: relativeDay(locale, task.due_at, { today: t('today'), tomorrow: t('tomorrow') }) })}` : ''}
                    </span>
                  </span>
                  <TaskStatusPill status={task.status} />
                </button>
              ))}
            </>
          )
        }
      </QueryView>
    </section>
  );
}

// ── Activity feed ────────────────────────────────────────────────

export function ActivityFeed({ projectId, episodeId, limit = 12 }: { projectId?: string; episodeId?: string; limit?: number }) {
  const { t, td, locale } = useT();
  const membership = useMembership();
  const [n, setN] = useState(limit);
  const allowed = can(membership?.role, 'tasks.view');
  const q = useActivity({ project_id: projectId, episode_id: episodeId, limit: n }, allowed);
  if (!allowed) return null;
  return (
    <section className="card" aria-labelledby={`feed-h-${projectId ?? episodeId ?? 'ws'}`}>
      <div className="card-head"><h2 id={`feed-h-${projectId ?? episodeId ?? 'ws'}`} className="h2">{t('actTitle')}</h2></div>
      <QueryView query={q}>
        {(d) =>
          d.items.length === 0 ? (
            <Empty>{t('actEmpty')}</Empty>
          ) : (
            <ul aria-label={t('actTitle')} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {d.items.map((it) => (
                <li key={it.id} style={{ display: 'grid', gridTemplateColumns: '26px minmax(0,1fr) auto', gap: 10, alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid var(--line)' }}>
                  <Avatar name={it.actor.kind === 'system' || !it.actor.name ? 'Castfeeds' : it.actor.name} size={26} colour={it.actor.kind === 'system' ? 'var(--raise)' : undefined} />
                  <span style={{ fontSize: 13, lineHeight: '18px' }}>
                    {it.task ? <Link to={`/tasks/${it.task.id}`}>{feedText({ t, td }, it)}</Link> : feedText({ t, td }, it)}
                    {!projectId && it.project ? <span className="muted"> · {it.project.title}</span> : null}
                  </span>
                  <span className="muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{ago(locale, it.at)}</span>
                </li>
              ))}
              {d.next_before ? (
                <li style={{ padding: '8px 16px' }}>
                  <button type="button" className="btn-link" onClick={() => setN((x) => Math.min(100, x + limit))}>{t('actMore')}</button>
                </li>
              ) : null}
            </ul>
          )
        }
      </QueryView>
    </section>
  );
}

// ── Presence on an episode ───────────────────────────────────────

/** The warning when someone else edits the same video. */
export function PresenceConflict({ presence, editingEditId, videoTitle }: { presence: Presence | null; editingEditId: string | null; videoTitle: string }) {
  const { t } = useT();
  if (!presence?.conflicts.length || !editingEditId) return null;
  return (
    <div role="alert" className="alert alert-warn row" style={{ alignItems: 'flex-start' }}>
      <TriangleAlert size={16} aria-hidden style={{ flex: 'none', marginTop: 2 }} />
      <span>{t('prConflict', { names: presence.conflicts.map((c) => c.name).join(', '), video: videoTitle })}</span>
    </div>
  );
}

/** Avatars of who is on this episode now, from `usePresence` (one heartbeat loop per page). */
export function PresenceBar({ presence, videoTitle }: { presence: Presence | null; videoTitle: (editId: string) => string }) {
  const { t } = useT();
  if (!presence || presence.viewers.length === 0) return null;
  const others = presence.viewers.filter((v) => !v.me);
  return (
    <div className="row" style={{ gap: 8 }} aria-live="polite">
      <Users size={14} aria-hidden className="muted" />
      <span className="avatar-stack" aria-label={t('prHere', { n: presence.viewers.length })} role="group">
        {presence.viewers.map((v) => {
          const what = v.editing_edit_id ? t('prEditing', { video: videoTitle(v.editing_edit_id) }) : t('prViewing');
          return <Avatar key={v.member_id} name={v.name} size={26} title={`${v.me ? t('prYou') : v.name} · ${what}`} />;
        })}
      </span>
      <span className="muted small">
        {t('prHere', { n: presence.viewers.length })}
        {others.length ? ` · ${others.map((o) => (o.editing_edit_id ? `${o.name} (${t('prEditing', { video: videoTitle(o.editing_edit_id) })})` : o.name)).join(', ')}` : ''}
      </span>
    </div>
  );
}

// ── Project task defaults ────────────────────────────────────────

export function TaskDefaults({ projectId }: { projectId: string }) {
  const { t, td } = useT();
  const membership = useMembership();
  const { people } = useTaskPeople();
  const q = useTaskAssignees(projectId, can(membership?.role, 'tasks.view'));
  const editable = can(membership?.role, 'tasks.assign');
  const save = useWsMutation((b: { producer: string | null; editor: string | null }) => api.tasks.setAssignees(projectId, b));
  if (!can(membership?.role, 'tasks.view')) return null;
  const pick = (which: 'producer' | 'editor', current: { producer: string | null; editor: string | null }) => (
    <select
      className="select"
      aria-label={which === 'producer' ? t('tkDefaultProducer') : t('tkDefaultEditor')}
      disabled={!editable || save.isPending}
      value={current[which] ?? ''}
      onChange={(e) => save.mutate({ ...current, [which]: e.target.value || null })}
    >
      <option value="">{t('tkNobody')}</option>
      {people.map((m: Member) => <option key={m.id} value={m.id}>{m.name} · {td(`role_${m.role}`, m.role)}</option>)}
    </select>
  );
  return (
    <div className="stack" style={{ gap: 8, padding: '12px 18px', borderBottom: '1px solid var(--line)' }}>
      <div className="stack" style={{ gap: 2 }}>
        <h3 className="h2" style={{ fontSize: 14 }}>{t('tkDefaults')}</h3>
        <span className="muted small">{t('tkDefaultsSub')}</span>
      </div>
      <QueryView query={q}>
        {(a) => {
          const current = { producer: a.producer?.member_id ?? null, editor: a.editor?.member_id ?? null };
          return (
            <div className="grid-2" style={{ gap: 10 }}>
              <label className="stack" style={{ gap: 4, fontSize: 13 }}><span className="muted">{t('tkDefaultProducer')}</span>{pick('producer', current)}</label>
              <label className="stack" style={{ gap: 4, fontSize: 13 }}><span className="muted">{t('tkDefaultEditor')}</span>{pick('editor', current)}</label>
            </div>
          );
        }}
      </QueryView>
      {save.error ? <ErrorBox error={save.error} /> : null}
    </div>
  );
}
